import React, {FC, useCallback, useRef, useState} from 'react';
import {Key} from 'ts-key-enum';

import {KeyboardActionsEnum} from '../../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {useLocalize} from '../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {defineMessage} from '../../../../../libs/shared/core/src/helpers/internationalization/message';
import {FrontKeyboardEvent, RendererOf} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {CalendarsPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {BoxedInput} from '../../../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {BoxedInputChildProps} from '../../../../../libs/shared/ui/src/components/boxedInputs/boxedInputConstants';
import {
  BoxedInputElement,
  BoxedInputElementTypesEnum,
} from '../../../../../libs/shared/ui/src/components/boxedInputs/boxedInputElement';
import {DropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {KeyboardShortcuts} from '../../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {tryParseVisualParticipantFromText} from '../../../../helpers/models/participantHelpers';
import {VisualParticipantModel} from '../../../../models/api/visualParticipantModel';
import {VisualParticipantsDropdownContainer} from './visualParticipantsDropdownContainer';

/*
 * Props.
 */

interface VisualParticipantsDropdownBoxProps {
  panelId: CalendarsPanelId;
  onVisualParticipantsSelect: (arg: ReadonlyArray<VisualParticipantModel>) => void;
  hasExistingAttendees: boolean;
  isUnsavedDraft: boolean;
  shouldEnableSearchKeyboardShortcut?: boolean;
  placeholder?: string;
  organizerAddress?: string;
  className?: string;
  inputBackgroundColor?: string;
  noMargin?: boolean;
  width?: number;
  renderDefaultDropdown?: RendererOf<DropdownProps>;
}

/*
 * Component.
 */

export const VisualParticipantsDropdownBox: FC<VisualParticipantsDropdownBoxProps> = (props) => {
  const {onVisualParticipantsSelect, className} = props;
  const localize = useLocalize();

  const [value, setValue] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const inputRef = useRef<BoxedInput>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  /*
   * Event handlers.
   */

  const onSelect: (arg: ReadonlyArray<VisualParticipantModel>) => void = useCallback(
    (visualParticipants) => {
      // Notify the parent and clear the input value.
      onVisualParticipantsSelect(visualParticipants);
      setValue('');
    },
    [onVisualParticipantsSelect],
  );

  const onInputKeyDown: (event: FrontKeyboardEvent) => void = useCallback(
    (event) => {
      if (event.key !== Key.Enter || event.shiftKey) {
        return;
      }

      // Handle the enter key by attempting to parse the participant from the text.
      event.preventDefault();
      const visualParticipant = tryParseVisualParticipantFromText(value);
      if (!visualParticipant) {
        return;
      }

      onVisualParticipantsSelect([visualParticipant]);
      setValue('');
    },
    [onVisualParticipantsSelect, value],
  );

  /*
   * Render.
   */

  const renderInputContent = (childProps: BoxedInputChildProps) => (
    <BoxedInputElement
      {...childProps}
      onChange={setValue}
      onFocus={() => setIsInputFocused(true)}
      onBlur={() => setIsInputFocused(false)}
      onKeyDown={onInputKeyDown}
      placeholder={
        props.placeholder ||
        localize(
          defineMessage({
            id: 'participants-dropdown-box',
            message: 'Add guests',
          }),
        )
      }
      type={BoxedInputElementTypesEnum.TEXT}
      value={value}
    />
  );

  const renderDropdown = () => (
    <VisualParticipantsDropdownContainer
      panelId={props.panelId}
      anchor={boxRef}
      query={value}
      onVisualParticipantsSelect={onSelect}
      hasExistingAttendees={props.hasExistingAttendees}
      width={props.width || 270}
      placement="bottom-end"
      organizerAddress={props.organizerAddress}
      isUnsavedDraft={props.isUnsavedDraft}
      renderDefaultDropdown={props.renderDefaultDropdown}
    />
  );

  return (
    <>
      <BoxedInput
        ref={inputRef}
        boxRef={boxRef}
        boxClassName={className}
        backgroundColor={props.inputBackgroundColor}
        noMargin={props.noMargin}
      >
        {(childProps) => renderInputContent(childProps)}
      </BoxedInput>
      {isInputFocused && renderDropdown()}
      {props.shouldEnableSearchKeyboardShortcut && (
        <KeyboardShortcuts
          handlers={{
            [KeyboardActionsEnum.CALENDAR_FOCUS_SEARCH]: () => inputRef?.current?.focus(),
          }}
        />
      )}
    </>
  );
};
